import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TableSortLabel,
  Checkbox,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import NoData from "../Nodata";
import { matchesColumnFilter } from "../../utils/tableUtils";
import { ACCENT_COLOR as accentColor } from "../../utils/constants";
import "./styles.scss";

const SKELETON_ROWS = 8;
const MIN_COL_WIDTH_PX = 140;
const MIN_CHECKBOX_COL_WIDTH_PX = 48;

const getColMinWidth = col => col.width || (col.key === "__select" ? MIN_CHECKBOX_COL_WIDTH_PX : MIN_COL_WIDTH_PX);

const SkeletonRow = ({ colCount, index }) => (
  <TableRow className="data-table__skeleton-row" style={{ animationDelay: `${index * 60}ms` }}>
    {Array.from({ length: colCount }).map((_, i) => (
      <TableCell key={i} className="data-table__cell">
        <div className="data-table__skeleton-cell" style={{ width: `${45 + (((index + i) * 17) % 40)}%` }} />
      </TableCell>
    ))}
  </TableRow>
);

SkeletonRow.propTypes = {
  colCount: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

const DataTable = ({
  columns = [],
  data = [],
  rows,
  visibleColumns = {},
  loading,
  isLoading,
  onSort,
  onFilter,
  filters: filtersProp,
  onFiltersChange,
  sortConfig: sortConfigProp,
  onSortConfigChange,
  onSortToggle,
  onRowClick,
  highlightedRowIndex,
  showFilters: showFiltersProp,
  noDataHeight,
  density = "comfortable",
  selectAllChecked,
  selectAllIndeterminate,
  onSelectAll,
}) => {
  const [internalFilters, setInternalFilters] = useState({});
  const filters = filtersProp !== undefined ? filtersProp : internalFilters;
  const [internalSortConfig, setInternalSortConfig] = useState({ key: null, direction: "asc" });

  const tableData = rows || data;
  const isTableLoading = isLoading !== undefined ? isLoading : loading;
  const showFilters = showFiltersProp !== undefined ? showFiltersProp : false;

  const sortConfig = useMemo(
    () =>
      sortConfigProp
        ? { key: sortConfigProp.key, direction: sortConfigProp.order || sortConfigProp.direction || "asc" }
        : internalSortConfig,
    [sortConfigProp, internalSortConfig]
  );

  const visibleColumnsList = useMemo(() => {
    if (!visibleColumns || Object.keys(visibleColumns).length === 0) {
      return columns;
    }
    return columns.filter(col => visibleColumns[col.key] !== false);
  }, [columns, visibleColumns]);

  const hasColumnWidths = visibleColumnsList.some(col => col.width);

  const filteredData = useMemo(() => {
    if (onFilter) return tableData;
    return tableData.filter(row => columns.every(col => matchesColumnFilter(col, row, filters)));
  }, [tableData, columns, filters, onFilter]);

  const sortedAndFilteredData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return filteredData || [];

    let result = [...filteredData];
    if (onSort || onSortToggle) return result;

    if (!sortConfig.key) return result;

    result.sort((a, b) => {
      const aValue = a?.[sortConfig.key];
      const bValue = b?.[sortConfig.key];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let compareA = aValue;
      let compareB = bValue;

      if (typeof aValue === "number" && typeof bValue === "number") {
        compareA = aValue;
        compareB = bValue;
      } else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        compareA = aValue ? 1 : 0;
        compareB = bValue ? 1 : 0;
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          compareA = dateA.getTime();
          compareB = dateB.getTime();
        } else {
          compareA = aValue.toLowerCase();
          compareB = bValue.toLowerCase();
        }
      } else {
        compareA = String(aValue).toLowerCase();
        compareB = String(bValue).toLowerCase();
      }

      if (compareA < compareB) return sortConfig.direction === "asc" ? -1 : 1;
      if (compareA > compareB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [filteredData, sortConfig, onSort, onSortToggle]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    } else {
      setInternalFilters(newFilters);
    }
    if (onFilter) onFilter(newFilters);
  };

  const handleSort = key => {
    if (onSortToggle) {
      onSortToggle(key);
      return;
    }

    let newDirection = "asc";
    if (internalSortConfig.key === key) {
      newDirection = internalSortConfig.direction === "asc" ? "desc" : "asc";
    }

    const newSortConfig = { key, direction: newDirection };
    setInternalSortConfig(newSortConfig);

    if (onSortConfigChange) onSortConfigChange(newSortConfig);
    if (onSort) onSort(key, newDirection);
  };

  const isSortable = col => col.sortable !== false;

  const getSortIcon = col => {
    if (sortConfig.key !== col.key) {
      return <ArrowUpDown size={13} className="data-table__sort-icon" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp size={14} className="data-table__sort-icon data-table__sort-icon--active" />
    ) : (
      <ChevronDown size={14} className="data-table__sort-icon data-table__sort-icon--active" />
    );
  };

  const densityClass =
    density === "compact" ? "data-table--compact" : density === "spacious" ? "data-table--spacious" : "";

  return (
    <Paper className={`data-table ${densityClass}`} elevation={0}>
      <TableContainer className="data-table__container">
        <div className="data-table__wrapper">
          <Table
            size="small"
            className={`data-table__table ${hasColumnWidths ? "data-table__table--fixed" : ""}`}
            aria-label="Data table"
            sx={{ minWidth: "100%", width: "max-content" }}
          >
            {hasColumnWidths && (
              <colgroup>
                {visibleColumnsList.map((col, idx) => (
                  <col key={idx} style={{ width: col.width }} />
                ))}
              </colgroup>
            )}

            <TableHead className="data-table__head">
              <TableRow className="data-table__head-row">
                {visibleColumnsList.map((col, idx) => (
                  <TableCell
                    key={col.key || idx}
                    className={`data-table__head-cell ${idx === 0 ? "data-table__head-cell--sticky" : ""}`}
                    style={{ minWidth: getColMinWidth(col) }}
                  >
                    {col.key === "__select" && onSelectAll ? (
                      <Checkbox
                        size="small"
                        checked={selectAllChecked}
                        indeterminate={selectAllIndeterminate}
                        onChange={onSelectAll}
                        sx={{
                          p: 0,
                          color: "#d1d5db",
                          "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: accentColor },
                        }}
                      />
                    ) : isSortable(col) ? (
                      <TableSortLabel
                        active={sortConfig.key === col.key}
                        direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSort(col.key);
                        }}
                        className="data-table__sort-label"
                        IconComponent={() => getSortIcon(col)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>

              <AnimatePresence>
                {showFilters && (
                  <motion.tr
                    className="data-table__filter-row"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                  >
                    {visibleColumnsList.map(col => (
                      <TableCell key={col.key} className="data-table__filter-cell" component="td">
                        {col.filterable !== false ? (
                          col.filterRender ? (
                            col.filterRender(filters[col.key], val => handleFilterChange(col.key, val))
                          ) : (
                            <TextField
                              size="small"
                              placeholder={col.label}
                              value={filters[col.key] || ""}
                              onChange={e => handleFilterChange(col.key, e.target.value)}
                              fullWidth
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "7px",
                                  backgroundColor: "var(--color-white, #fff)",
                                  fontSize: "0.76rem",
                                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                                  "& fieldset": { borderColor: "#e8eaf0" },
                                  "&:hover fieldset": { borderColor: "#d1d5db" },
                                  "&.Mui-focused fieldset": {
                                    borderColor: accentColor,
                                    borderWidth: "1.5px",
                                  },
                                  "&.Mui-focused": {
                                    boxShadow: "0 0 0 3px rgba(244,124,32,0.08)",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  color: "#111827",
                                  padding: "6px 10px",
                                  "&::placeholder": { color: "#9ca3af", opacity: 1 },
                                },
                              }}
                            />
                          )
                        ) : null}
                      </TableCell>
                    ))}
                  </motion.tr>
                )}
              </AnimatePresence>
            </TableHead>

            <TableBody className="data-table__body">
              {/* Skeleton loading rows */}
              {isTableLoading &&
                Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <SkeletonRow key={`skel-${i}`} colCount={visibleColumnsList.length} index={i} />
                ))}

              {/* Empty state */}
              {!isTableLoading && sortedAndFilteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumnsList.length} className="data-table__no-data" align="center">
                    <NoData height={noDataHeight} />
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isTableLoading &&
                sortedAndFilteredData.length > 0 &&
                sortedAndFilteredData.map((row, rowIndex) => (
                  <TableRow
                    key={row.id || row.email || row.featureId || row.key || rowIndex}
                    onClick={() => onRowClick?.(row, rowIndex)}
                    className={`data-table__row ${onRowClick ? "data-table__row--clickable" : ""} ${
                      highlightedRowIndex === rowIndex ? "data-table__row--highlighted" : ""
                    } ${rowIndex % 2 === 0 ? "data-table__row--even" : "data-table__row--odd"}`}
                    style={{ animationDelay: `${Math.min(rowIndex * 20, 400)}ms` }}
                  >
                    {visibleColumnsList.map((col, colIndex) => {
                      const value = row?.[col.key];
                      return (
                        <TableCell
                          key={col.key || colIndex}
                          className={`data-table__cell ${colIndex === 0 ? "data-table__cell--sticky" : ""}`}
                          style={{ minWidth: getColMinWidth(col) }}
                        >
                          {col.render ? col.render(value, row, rowIndex) : (value ?? "—")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </TableContainer>
    </Paper>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  rows: PropTypes.array,
  visibleColumns: PropTypes.object,
  loading: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSort: PropTypes.func,
  onFilter: PropTypes.func,
  filters: PropTypes.object,
  sortConfig: PropTypes.object,
  onSortToggle: PropTypes.func,
  onRowClick: PropTypes.func,
  highlightedRowIndex: PropTypes.number,
  showFilters: PropTypes.bool,
  noDataHeight: PropTypes.string,
  density: PropTypes.oneOf(["compact", "comfortable", "spacious"]),
  selectAllChecked: PropTypes.bool,
  selectAllIndeterminate: PropTypes.bool,
  onSelectAll: PropTypes.func,
  onFiltersChange: PropTypes.func,
  onSortConfigChange: PropTypes.func,
};

export default DataTable;
